/**
 * Quem atende o espelho em cada plataforma.
 *
 * A detecção é por farejamento de `window`, e não por importar `@capacitor/core`
 * ou `@tauri-apps/api`, pelo mesmo motivo que `erros.ts` já faz assim: importar
 * o pacote do Tauri na web arrasta código que nunca vai rodar, e importar o do
 * Capacitor registra um objeto global só para perguntar onde estamos.
 *
 * Os dois `import()` são dinâmicos de propósito. Assim o pacote da web nunca
 * baixa nem executa o código nativo: o empacotador põe cada um no seu pedaço,
 * e no navegador nenhum dos dois é buscado.
 */

import type { ArmazenamentoDuravel } from "./porta";

export type Plataforma = "tauri" | "capacitor" | "web";

/** O que cada casca nativa injeta na janela antes do jogo carregar. */
interface JanelaNativa {
  __TAURI_INTERNALS__?: unknown;
  androidBridge?: unknown;
  webkit?: { messageHandlers?: { bridge?: unknown } };
}

export function detectarPlataforma(): Plataforma {
  // Sem `window` estamos no Web Worker ou em Node, e nenhum dos dois plugins
  // funciona nesses lugares. Não é uma proteção defensiva, é o motivo de o
  // espelho viver na thread da interface.
  if (typeof window === "undefined") return "web";

  const janela = window as unknown as JanelaNativa;
  if (janela.__TAURI_INTERNALS__) return "tauri";

  // As mesmas duas marcas que o `@capacitor/core` usa para decidir se está no
  // nativo. Farejar direto evita carregar o pacote dele só para essa resposta.
  if (janela.androidBridge) return "capacitor";
  if (janela.webkit?.messageHandlers?.bridge) return "capacitor";

  return "web";
}

/**
 * Devolve `null` no navegador puro, e isso não é falha.
 *
 * Na web não existe pasta fora do alcance do navegador para onde copiar, então
 * o espelho não tem o que fazer. Quem chama trata o `null` pedindo
 * armazenamento persistente ao navegador, que é o equivalente possível ali.
 */
export async function abrirArmazenamentoDuravel(): Promise<ArmazenamentoDuravel | null> {
  switch (detectarPlataforma()) {
    case "tauri": {
      const { abrirSqliteTauri } = await import("./nativo-tauri");
      return abrirSqliteTauri();
    }
    case "capacitor": {
      const { abrirSqliteCapacitor } = await import("./nativo-capacitor");
      return abrirSqliteCapacitor();
    }
    case "web":
      return null;
  }
}
